// Analytics LiveView Hook
// Enables server-side tracking from Phoenix LiveView components

import Analytics, { Goals } from '../lib/analytics';

const AnalyticsHook = {
  mounted() {
    // Listen for track events from the server
    this.handleEvent("analytics-track-goal", ({ goal, props }) => {
      Analytics.trackGoal(goal, props);
    });

    this.handleEvent("analytics-track-event", ({ event, properties }) => {
      Analytics.trackEvent(event, properties);
    });

    this.handleEvent("analytics-track-revenue", ({ goal, amount, currency, props }) => {
      Analytics.trackRevenue(goal, amount, currency, props);
    });

    // Handle form tracking if this element is a form
    if (this.el.tagName === 'FORM' && this.el.dataset.trackGoal) {
      const originalSubmitHandler = this.el.onsubmit;
      
      this.el.onsubmit = (e) => {
        // Track the form submission start
        const startGoal = this.el.dataset.trackGoal;
        const startProps = this.el.dataset.trackProps ? 
          JSON.parse(this.el.dataset.trackProps) : {};
        
        Analytics.trackGoal(startGoal, {
          path: window.location.pathname,
          ...startProps
        });

        // Call original handler if it exists
        if (originalSubmitHandler) {
          return originalSubmitHandler.call(this.el, e);
        }
      };
    }

    // Handle click tracking if this element has data-track-click
    if (this.el.dataset.trackClick) {
      this.el.addEventListener('click', () => {
        const goal = this.el.dataset.trackClick;
        const props = this.el.dataset.trackProps ? 
          JSON.parse(this.el.dataset.trackProps) : {};
        
        Analytics.trackGoal(goal, {
          path: window.location.pathname,
          element: this.el.tagName.toLowerCase(),
          text: this.el.textContent.trim().substring(0, 50),
          ...props
        });
      });
    }

    // Track page view with custom props if specified
    if (this.el.dataset.trackPageview) {
      const props = this.el.dataset.pageviewProps ? 
        JSON.parse(this.el.dataset.pageviewProps) : {};
      
      Analytics.trackEvent('pageview', props);
    }

    // Form abandonment tracking
    if (this.el.tagName === 'FORM' && this.el.dataset.trackAbandonment) {
      let lastFocusedField = null;
      let formStarted = false;

      // Track when form interaction starts
      this.el.addEventListener('focusin', (e) => {
        if (!formStarted && e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          formStarted = true;
          lastFocusedField = e.target.name || e.target.id || 'unknown';
        }
      });

      // Track last focused field
      this.el.addEventListener('focusout', (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          lastFocusedField = e.target.name || e.target.id || 'unknown';
        }
      });

      // Track abandonment on page unload
      window.addEventListener('beforeunload', () => {
        if (formStarted && !this.formSubmitted) {
          const formName = this.el.dataset.trackAbandonment;
          Analytics.trackFormAbandonment(formName, lastFocusedField);
        }
      });

      // Mark form as submitted to prevent false abandonment tracking
      this.el.addEventListener('submit', () => {
        this.formSubmitted = true;
      });
    }
  },

  destroyed() {
    // Cleanup if needed
  }
};

// Helper functions for use in LiveView modules
export const AnalyticsHelpers = {
  // Generate event data for common scenarios
  registrationStarted: () => ({
    event: "analytics-track-goal",
    goal: Goals.REGISTRATION_STARTED,
    props: {}
  }),

  userRegistered: (plan = 'free') => ({
    event: "analytics-track-goal", 
    goal: Goals.USER_REGISTERED,
    props: { plan }
  }),

  newsletterSignup: () => ({
    event: "analytics-track-goal",
    goal: Goals.NEWSLETTER_SIGNUP,
    props: {}
  }),

  newsletterConfirmed: () => ({
    event: "analytics-track-goal",
    goal: Goals.NEWSLETTER_CONFIRMED,
    props: {}
  }),

  contactFormSubmitted: (inquiryType) => ({
    event: "analytics-track-goal",
    goal: Goals.CONTACT_FORM_SUBMITTED,
    props: { inquiry_type: inquiryType }
  }),

  subscriptionCreated: (plan, amount, currency = 'USD') => ({
    event: "analytics-track-revenue",
    goal: Goals.SUBSCRIPTION_CREATED,
    amount,
    currency,
    props: { plan }
  }),

  featureUsed: (featureName) => ({
    event: "analytics-track-goal",
    goal: Goals.FEATURE_USED,
    props: { feature: featureName }
  })
};

export default AnalyticsHook;